import * as Sentry from '@sentry/react';
import { ClientEvents, ClientTrackableEvents } from 'common/events/ClientEvents';
import { parse as parseQueryString } from 'query-string';
import React from 'react';
import { Route, RouteChildrenProps, RouteProps } from 'react-router';
import { useSearchQueryReducer } from 'src/pages/consumer/components/search/store/hooks/useSearchQuery';
import { withErrorBoundary } from 'src/shared/errors/withErrorBoundary';
import { useTracker } from 'src/shared/hooks/useTracker';
import { withLoading } from 'src/shared/routing/withLoading';
import { getPreferredColorTheme } from 'src/shared/utils/ColorThemeUtils';
import { useIsOnSearchPage } from 'src/shared/utils/SearchUtils';
import { LazyComponent } from 'src/util/retry';
import { usePrevious } from '../shared/hooks/usePreviousHook';

type UseRouteTrackingOptions = {
  // If true, suppress sending PAGE_VIEW for this navigation
  suppressPageView?: boolean;
  // If provided, this will run before PAGE_VIEW is sent (useful to log experiment exposure first)
  beforePageView?: () => Promise<void> | void;
};

/**
 * Hook: Tracks PAGE_VIEW on route changes. Focused solely on analytics dispatch.
 */
const usePageViewTracking = (
  location: RouteChildrenProps['location'],
  match: RouteChildrenProps['match'],
  options?: UseRouteTrackingOptions
) => {
  const tracker = useTracker();
  const preferredColorTheme = getPreferredColorTheme();
  // Remember the last routeKey we successfully tracked to avoid duplicate PAGE_VIEWs from harmless re-renders
  const lastTrackedRouteKeyRef = React.useRef<string | null>(null);
  // Keep a stable reference to the latest beforePageView without causing the PAGE_VIEW effect to re-run
  const latestBeforeRef = React.useRef<(() => Promise<void> | void) | undefined>(undefined);

  React.useEffect(() => {
    latestBeforeRef.current = options?.beforePageView;
  }, [options?.beforePageView]);

  const routeKey = `${location.pathname}${location.search}`;
  React.useEffect(() => {
    // Track whether this effect has become stale due to unmount or dependency change
    // while awaiting async work above (e.g., beforePageView). If it has, we skip sending
    // PAGE_VIEW to avoid double-sends or running after unmount.
    let isStale = false;
    const shouldSuppress = Boolean(options?.suppressPageView);

    // Optional debug logging toggle
    const DEBUG = typeof window !== 'undefined' && localStorage.getItem('DEBUG_ROUTE_TRACKING') === '1';
    const logDiagnostic = (label: string, data?: Record<string, any>) => {
      if (!DEBUG) return;
      // 1) Send to Sentry for observability
      try {
        Sentry.addBreadcrumb({ category: 'route-tracking', level: 'info', message: label, data });
        Sentry.captureMessage(`[route-tracking] ${label}`, 'info');
      } catch (err) {
        // Avoid recursive Sentry usage on failure; fall back to console in debug mode
        console.warn('[route-tracking] Failed to log diagnostic to Sentry', { label, err });
      }
      // 2) Optionally forward to a backend endpoint (which can land in LogDNA) if configured
      try {
        const url = process.env.REACT_APP_ROUTE_TRACKING_LOG_URL;
        if (url) {
          const body = JSON.stringify({
            label,
            data: data ?? null,
            routeKey,
            ts: Date.now(),
          });
          if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
          } else {
            fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body,
              keepalive: true,
            }).catch((err) => {
              console.warn('[route-tracking] Failed to send backend diagnostic', err);
            });
          }
        }
      } catch (err) {
        console.warn('[route-tracking] Failed in diagnostic logging block', err);
      }
    };

    const run = async () => {
      if (shouldSuppress) {
        logDiagnostic('Suppressed PAGE_VIEW', {
          route: routeKey,
          pattern: match?.path ?? null,
          params: match?.params ?? null,
        });
        return;
      }

      // Suppress duplicate PAGE_VIEWs for the same routeKey (e.g., re-renders causing effect re-run)
      // Do this BEFORE running any pre-track logic to avoid unnecessary work.
      if (lastTrackedRouteKeyRef.current === routeKey) {
        logDiagnostic('Duplicate PAGE_VIEW suppressed', { route: routeKey });
        return;
      }

      try {
        const before = latestBeforeRef.current;
        if (before) {
          await before();
        }
      } catch (e) {
        // Do not block PAGE_VIEW if pre-track fails; log and continue
        Sentry.captureException(e, {
          level: 'warning',
          tags: { area: 'tracking', component: 'tracked-route' },
          extra: { note: 'beforePageView callback threw in usePageViewTracking' },
        });
      }
      // If the effect was cleaned up (route changed/unmounted) while awaiting above, do nothing.
      if (isStale) return;

      const viewWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const viewHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

      const payload: ClientTrackableEvents[ClientEvents.PAGE_VIEW] = {
        route: routeKey,
        queryParams: location.search ? parseQueryString(location.search) : null,
        pattern: match?.path ?? null,
        params: match?.params ?? null,
        preferredColorTheme,
        viewportDimensions: {
          widthPixels: viewWidth,
          heightPixels: viewHeight,
        },
      };

      logDiagnostic('PAGE_VIEW dispatched', payload);

      await tracker.track(ClientEvents.PAGE_VIEW, payload);
      // Mark this routeKey as tracked to avoid duplicates on subsequent harmless re-renders
      lastTrackedRouteKeyRef.current = routeKey;
    };

    run();
    return () => {
      // Mark this effect as stale so any in-flight async work will no-op when it resumes.
      isStale = true;
    };
    // Re-run only when the route changes, the tracker instance changes, the theme changes, or suppression toggles
  }, [routeKey, tracker, preferredColorTheme, options?.suppressPageView]);
};

/**
 * Hook: Clears search query when navigating away from search pages.
 */
const useClearSearchQueryOnNav = (location: RouteChildrenProps['location']) => {
  const { isOnSearchPage, isOnSearchPageMobile } = useIsOnSearchPage();
  const { changeSearchQuery, searchQuery: query } = useSearchQueryReducer();
  const prevPathname = usePrevious(location.pathname);

  React.useEffect(() => {
    if (
      !isOnSearchPage &&
      !isOnSearchPageMobile &&
      prevPathname &&
      location.pathname !== prevPathname &&
      query !== ''
    ) {
      changeSearchQuery('');
    }
  }, [isOnSearchPage, isOnSearchPageMobile, prevPathname, location.pathname, query, changeSearchQuery]);
};

export const useRouteTracking = (
  location: RouteChildrenProps['location'],
  match: RouteChildrenProps['match'],
  options?: UseRouteTrackingOptions
) => {
  usePageViewTracking(location, match, options);
  useClearSearchQueryOnNav(location);
};

export type RoutePropsOnlyComponent = Omit<RouteProps, 'render' | 'children'> & {
  component: React.ComponentType<any> | LazyComponent<any>;
  // Optional per-route tracking customizations
  suppressPageView?: boolean | ((props: RouteChildrenProps) => boolean);
  beforePageView?: (props: RouteChildrenProps) => Promise<void> | void;
};

export const withPageViewTracking: (
  Base: React.ComponentType<RouteChildrenProps>,
  opts?: Pick<RoutePropsOnlyComponent, 'suppressPageView' | 'beforePageView'>
) => React.FC<RouteChildrenProps> =
  (Base: React.ComponentType<RouteChildrenProps>, opts) => (props: RouteChildrenProps) => {
    const suppress =
      typeof opts?.suppressPageView === 'function' ? opts?.suppressPageView(props) : opts?.suppressPageView;
    const before = opts?.beforePageView ? () => opts.beforePageView!(props) : undefined;
    useRouteTracking(props.location, props.match, {
      suppressPageView: suppress,
      beforePageView: before,
    });
    return <Base {...props} />;
  };

export const TrackedRouteWithErrorBoundary: React.FC<RoutePropsOnlyComponent> = (props) => {
  const Component = React.useMemo(
    () =>
      withPageViewTracking(withErrorBoundary(withLoading(props.component)), {
        suppressPageView: props.suppressPageView,
        beforePageView: props.beforePageView,
      }),
    // Intentionally only depend on component identity. Inline function props (e.g., landingPageVariant, beforePageView)
    // would otherwise change identity each render and recreate the wrapper, causing remounts and duplicate PAGE_VIEWs.
    [props.component]
  );
  // Exclude custom tracking props from being passed to Route
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { suppressPageView, beforePageView, ...routeProps } = props as any;
  return <Route {...(routeProps as RouteProps)} component={Component} />;
};
